/** @jsx jsx */
import * as React from 'react';
import { jsx } from '@compiled/react';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import { css } from '@atlaskit/css';
import AkHeading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

import type { ContainerPadding } from './styled';
import { StyledIconCardLayout, Description, HeadingContent } from './styled';

export interface Props {
    heading?: React.ReactNode;
    description: React.ReactNode;
    /**
     * Will clamp the heading to 1 line when lineClamp={1} or greater.
     * Will clamp description to the number of lines specified.
     * 0 or undefined = no clamping.
     * 1 = 1 line.
     * 2 = 2 lines. etc.
     */
    lineClamp?: number;
    className?: string;
    dataTestId?: string;
    icon?: string | React.ReactNode;
    /**
     * Used to switch the style of padding inside the container.
     * "default" is 3 grid lengths, "tight" is 2 grid lengths.
     * This only affects left/right padding.
     */
    containerPadding?: ContainerPadding;
    editRequestTypeOnRequestList?: React.ReactElement;
}

const GroupBox: React.FunctionComponent<Props> = ({
    heading,
    description,
    icon,
    editRequestTypeOnRequestList,
    lineClamp = 0,
    ...props
}) => {
    const showEditRequestButton = !!editRequestTypeOnRequestList;
    const content = showEditRequestButton ? (
        <div css={GroupBoxWithAdditionalButtonLayout}>
            <div css={OverlayButtonLayout}>{editRequestTypeOnRequestList}</div>
            {heading && (
                <AkHeading size="xsmall" as="h3">
                    <HeadingContent lineClamp={!!lineClamp}>{heading}</HeadingContent>
                </AkHeading>
            )}
            <Description aria-hidden lineClamp={lineClamp}>
                {description}
            </Description>
        </div>
    ) : (
        <React.Fragment>
            {heading && (
                <AkHeading size="xsmall" as="h3">
                    <HeadingContent lineClamp={!!lineClamp}>{heading}</HeadingContent>
                </AkHeading>
            )}
            <Description aria-hidden lineClamp={lineClamp}>
                {description}
            </Description>
        </React.Fragment>
    );

    return (
        <StyledIconCardLayout
            {...props}
            leftIcon={icon && (React.isValidElement(icon) ? icon : <AutoScaledImage src={icon as string} alt="" />)}
            content={content}
        />
    );
};

const GroupBoxWithAdditionalButtonLayout = css({
    position: 'relative',
});

const OverlayButtonLayout = css({
    position: 'absolute',
    right: token('space.0', '0px'),
    top: token('space.0', '0px'),
});

export default GroupBox;
